import { Component, OnInit, OnDestroy } from "@angular/core";
import { CampaignService } from 'src/app/_services/campaign.service';
import { LocalStorageService } from 'src/app/_services/local-storage.service';
import { Subscription, timer } from 'rxjs';


@Component({
  selector: 'app-campaigns-limits',
  templateUrl: './campaigns-limits.component.html',
  styleUrls: ['./campaigns-limits.component.scss'],
  standalone: false
})
export class CampaignsLimitsComponent implements OnInit, OnDestroy {

  // 0 = Safe, 1 = Moderate, 2 = Risky
  dailyLimit: number = 0;
  hourlyLimit: number = 0;
  infoBarVisible: boolean = false;

  editModalOpen: boolean = false;
  pendingLevel: number = 0;

  readonly levelConfig = [
    {
      key: 'safe',
      label: 'Safe',
      daily: '≤ 60',
      hourly: '≤ 10',
      note: 'Ideal for warming up new inboxes. Closely mimics natural sending patterns.'
    },
    {
      key: 'moderate',
      label: 'Moderate',
      daily: '61–150',
      hourly: '11–20',
      note: 'Suitable for established accounts with a healthy sender reputation.'
    },
    {
      key: 'risky',
      label: 'Risky',
      daily: '151–400',
      hourly: '21–35',
      note: 'High-volume sending. Risk of spam flags if inbox reputation is below average.'
    },
  ];

  private hideInfoTimerSub: Subscription;

  constructor(private campaignService: CampaignService, private localstorageservice: LocalStorageService) { }

  ngOnInit(): void {
    const usernumber = this.localstorageservice.getItem('usernumber') || '';
    this.fetchUserLimits(usernumber);
  }

  ngOnDestroy(): void {
    if (this.hideInfoTimerSub) {
      this.hideInfoTimerSub.unsubscribe();
    }
  }

  // ── Computed display values ──────────────────────────────────

  get activeLevel(): number {
    return Math.min(this.dailyLimit, this.levelConfig.length - 1);
  }

  get activeLevelLabel(): string {
    return this.levelConfig[this.activeLevel].label;
  }

  get activeLevelKey(): string {
    return this.levelConfig[this.activeLevel].key;
  }

  get activeDailyDisplay(): string {
    return this.levelConfig[this.activeLevel].daily;
  }

  get activeHourlyDisplay(): string {
    return this.levelConfig[this.activeLevel].hourly;
  }

  get activeLevelNote(): string {
    return this.levelConfig[this.activeLevel].note;
  }

  // ── Data fetching ────────────────────────────────────────────

  fetchUserLimits(usernumber: string) {
    if (!usernumber) return;
    this.campaignService.fetchUserLimits(usernumber).subscribe((res: any) => {
      if (res && res.data) {
        this.dailyLimit = res.data.email_limit_per_day != null ? res.data.email_limit_per_day : 0;
        this.hourlyLimit = res.data.email_limit_per_hour != null ? res.data.email_limit_per_hour : 0;
      }
    }, (_err) => {});
  }

  // ── Modal control ────────────────────────────────────────────

  openEditModal() {
    this.pendingLevel = this.activeLevel;
    this.editModalOpen = true;
  }

  closeEditModal() {
    this.editModalOpen = false;
  }

  onOverlayClick(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('cl-modal-overlay')) {
      this.closeEditModal();
    }
  }

  saveFromModal() {
    this.dailyLimit = this.pendingLevel;
    this.hourlyLimit = this.pendingLevel;
    this.closeEditModal();
    this.onLimitChange();
  }

  // ── API save ─────────────────────────────────────────────────

  onLimitChange() {
    const usernumber = this.localstorageservice.getItem('usernumber') || '';
    const payload = {
      usernumber,
      dailyLimit: this.dailyLimit,
      hourlyLimit: this.hourlyLimit
    };

    this.campaignService.updateUserLimits(payload).subscribe((_res: any) => {
      this.showInfoBar();
    }, (_err) => {
      this.showInfoBar();
    });
  }

  showInfoBar() {
    this.infoBarVisible = true;
    if (this.hideInfoTimerSub) {
      this.hideInfoTimerSub.unsubscribe();
    }
    this.hideInfoTimerSub = timer(5000).subscribe(() => { this.infoBarVisible = false; });
  }

}
